import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Seo } from "../components/site/Seo";
import { ClosingCta } from "../components/site/Blocks";
import { ARTICLES, getArticle } from "../data/insights";
import { getService } from "../data/services";
import { DraftBadge } from "./Insights";
import NotFound from "./NotFound";

export default function Article() {
  const { slug } = useParams();
  const a = getArticle(slug);
  if (!a) return <NotFound />;
  const service = a.service && getService(a.service);
  const related = ARTICLES.filter((r) => r.slug !== a.slug).sort((x, y) => (y.category === a.category) - (x.category === a.category)).slice(0, 3);
  const ld = { "@context": "https://schema.org", "@type": "Article", headline: a.title, description: a.excerpt, publisher: { "@type": "Organization", name: "Trusted Leap" } };

  return (
    <>
      <Seo title={a.title} description={a.excerpt} path={`/insights/${a.slug}`} type="article" jsonLd={ld} />
      <article>
        <header className="border-b border-slate-200 bg-[#F4F6F8]">
          <div className="container-x max-w-4xl py-16 lg:py-20">
            <Link to="/insights" className="link-arrow" data-testid="article-back-link"><ArrowLeft className="h-4 w-4" /> All insights</Link>
            <div className="mt-10 flex flex-wrap items-center gap-3"><span className="text-xs font-semibold uppercase tracking-[0.16em] text-[#2563EB]">{a.category}</span><DraftBadge /><span className="text-xs text-slate-500">{a.readTime}</span></div>
            <h1 className="mt-6 font-display text-4xl font-semibold leading-[1.08] tracking-[-0.03em] text-[#0B1220] sm:text-5xl">{a.title}</h1>
            <p className="lead mt-6">{a.excerpt}</p>
          </div>
        </header>
        <div className="container-x max-w-4xl py-16">
          <div className="aspect-[16/8] overflow-hidden rounded-lg"><img src={a.image} alt="" className="h-full w-full object-cover" /></div>
          <div className="mt-6 rounded-md border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900" data-testid="article-draft-notice">
            Sample draft for layout demonstration only. This content is general in nature, has not been editorially or professionally reviewed, and does not constitute professional advice.
          </div>
          <div className="mx-auto mt-14 max-w-2xl">
            {a.body.map((b) => (
              <section key={b.h} className="mb-10">
                <h2 className="font-display text-2xl font-semibold tracking-tight text-[#0B1220]">{b.h}</h2>
                <p className="mt-4 text-[17px] leading-[1.8] text-slate-600">{b.p}</p>
              </section>
            ))}
            {service && (
              <div className="mt-14 border-t border-slate-200 pt-8">
                <p className="text-sm text-slate-500">Related service</p>
                <Link to={`/services/${service.slug}`} className="link-arrow mt-2 text-lg" data-testid="article-service-link">{service.name} through Trusted Leap <ArrowRight className="h-4 w-4" /></Link>
              </div>
            )}
          </div>
        </div>
      </article>
      <section className="border-t border-slate-200 bg-white py-20">
        <div className="container-x">
          <p className="eyebrow">Related articles</p>
          <div className="mt-10 grid gap-8 md:grid-cols-3">
            {related.map((r) => (
              <Link key={r.slug} to={`/insights/${r.slug}`} className="group border-t border-slate-200 pt-6" data-testid={`article-related-${r.slug}`}>
                <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[#2563EB]">{r.category}</span>
                <h3 className="mt-3 font-display text-lg font-semibold leading-snug text-[#0B1220] group-hover:text-[#2563EB]">{r.title}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <ClosingCta />
    </>
  );
}
